"use client";

import { useMemo, useRef, useCallback, useState, useEffect } from "react";

/**
 * Canvas渲染配置
 */
const CANVAS_CONFIG = {
  width: 400,
  height: 200,
  fontSize: 48,
  fontWeight: "bold",
} as const;

interface TextPreviewProps {
  text: string;
  selectedFont: string;
}

/**
 * 文本预览组件 - 纯Canvas预览功能
 * 只负责文本到Canvas的渲染，不包含点云生成逻辑
 */
export function TextPreview({ text, selectedFont }: TextPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null);

  // Canvas文本渲染函数
  const renderTextToCanvas = useCallback(() => {
    if (!text.trim()) {
      setCanvasDataUrl(null);
      return;
    }

    // 创建离屏Canvas
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 设置Canvas尺寸
    canvas.width = CANVAS_CONFIG.width;
    canvas.height = CANVAS_CONFIG.height;

    // 清空Canvas
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 设置文本样式
    ctx.fillStyle = "#ffffff";
    ctx.font = `${CANVAS_CONFIG.fontWeight} ${CANVAS_CONFIG.fontSize}px ${selectedFont}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // 绘制文本
    const x = canvas.width / 2;
    const y = canvas.height / 2;
    ctx.fillText(text, x, y);

    // 保存Canvas为DataURL
    const dataUrl = canvas.toDataURL();
    setCanvasDataUrl(dataUrl);
  }, [text, selectedFont]);

  // 当文本或字体变化时重新渲染Canvas
  useEffect(() => {
    renderTextToCanvas();
  }, [renderTextToCanvas]);

  return (
    <div className="space-y-3">
      {/* Canvas Preview - 固定 2:1 宽高比 (400:200) */}
      <div className="w-full aspect-[2/1]">
        {canvasDataUrl ? (
          <img
            src={canvasDataUrl}
            alt="Text render preview"
            className="w-full h-full object-contain rounded-md"
          />
        ) : (
          <div className="w-full h-full border-2 border-dashed border-muted rounded-md flex items-center justify-center">
            <p className="text-xs text-muted-foreground">
              {text.trim() 
                ? "Rendering preview..."
                : "Enter text to see preview"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}